import { lazy, Suspense, type ComponentType } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { OnboardingRedirector } from './onboarding/OnboardingRedirector';
import { ToastProvider } from './components/Toast';
import { AppShellSpa } from './AppShellSpa';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { AuthSetupPage } from './pages/AuthSetupPage';
import { AuthLoginPage } from './pages/AuthLoginPage';
import { ActiveRuntimeProvider } from './runtime/ActiveRuntimeContext';
import { ActiveWorkspaceProvider } from './runtime/ActiveWorkspaceContext';
import { WorkspaceAccessRedirector } from './runtime/WorkspaceAccessRedirector';
import { WorkspaceBrandingProvider } from './runtime/WorkspaceBrandingContext';
import { WorkspaceFeaturesProvider } from './runtime/WorkspaceFeaturesContext';
import { NotOnCloudPage, isCloudHiddenPath, useCloud } from './cloud';
import { ErrorBoundary } from './components/ErrorBoundary';
import { EmptyState } from '@pipebase/ui';

// Each page loads when its route is first visited, so the first load carries
// the shell and the page asked for, not every page and both flow editors.
// After a deploy, a tab opened before it asks for page files that are gone:
// it reloads once to get the new ones, and if that fails too the page area
// says so with a Reload button (PageError), keeping the shell and the nav.
const CHUNK_RELOAD_KEY = 'pipebase:chunk-reload';
export function withChunkReload<M>(load: () => Promise<M>, reload: () => void = () => window.location.reload()): () => Promise<M> {
  return () =>
    load().then(
      (m) => {
        try {
          sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        } catch {
          /* storage may be off */
        }
        return m;
      },
      (err: unknown) => {
        let reloaded = false;
        try {
          reloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
          if (!reloaded) sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        } catch {
          reloaded = true;
        }
        if (!reloaded) {
          reload();
          return new Promise<M>(() => undefined);
        }
        throw err;
      },
    );
}
function lazyPage<T extends ComponentType<object>>(load: () => Promise<{ default: T }>) {
  return lazy(withChunkReload(load));
}
const FlowList = lazyPage(() => import('./pages/FlowList').then((m) => ({ default: m.FlowList })));
const FlowEditor = lazyPage(() => import('./pages/FlowEditor').then((m) => ({ default: m.FlowEditor })));
const FlowEditorNext = lazyPage(() => import('./pages/FlowEditorNext').then((m) => ({ default: m.FlowEditorNext })));
const Settings = lazyPage(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));
const FlowCutoverPage = lazyPage(() => import('./pages/FlowCutoverPage').then((m) => ({ default: m.FlowCutoverPage })));
const CutoverOverviewPage = lazyPage(() => import('./pages/CutoverOverviewPage').then((m) => ({ default: m.CutoverOverviewPage })));
const RunHistoryPage = lazyPage(() => import('./pages/RunHistoryPage').then((m) => ({ default: m.RunHistoryPage })));
const FlowSloPage = lazyPage(() => import('./pages/FlowSloPage').then((m) => ({ default: m.FlowSloPage })));
const ValueMappingListPage = lazyPage(() => import('./pages/ValueMappingListPage').then((m) => ({ default: m.ValueMappingListPage })));
const ValueMappingEditPage = lazyPage(() => import('./pages/ValueMappingEditPage').then((m) => ({ default: m.ValueMappingEditPage })));
const MessageMappingListPage = lazyPage(() => import('./pages/MessageMappingListPage').then((m) => ({ default: m.MessageMappingListPage })));
const MessageMappingEditPage = lazyPage(() => import('./pages/MessageMappingEditPage').then((m) => ({ default: m.MessageMappingEditPage })));
const EventDesignListPage = lazyPage(() => import('./pages/EventDesignListPage').then((m) => ({ default: m.EventDesignListPage })));
const EventDesignEditPage = lazyPage(() => import('./pages/EventDesignEditPage').then((m) => ({ default: m.EventDesignEditPage })));
const EventCatalogPage = lazyPage(() => import('./pages/EventCatalogPage').then((m) => ({ default: m.EventCatalogPage })));
const RuntimeListPage = lazyPage(() => import('./pages/RuntimeListPage').then((m) => ({ default: m.RuntimeListPage })));
const RuntimeEditPage = lazyPage(() => import('./pages/RuntimeEditPage').then((m) => ({ default: m.RuntimeEditPage })));
const FleetMatrixPage = lazyPage(() => import('./pages/FleetMatrixPage').then((m) => ({ default: m.FleetMatrixPage })));
const PoolNewPage = lazyPage(() => import('./pages/PoolNewPage').then((m) => ({ default: m.PoolNewPage })));
const PoolDetailPage = lazyPage(() => import('./pages/PoolDetailPage').then((m) => ({ default: m.PoolDetailPage })));
const ParametersListPage = lazyPage(() => import('./pages/ParametersListPage').then((m) => ({ default: m.ParametersListPage })));
const RolesPage = lazyPage(() => import('./pages/RolesPage').then((m) => ({ default: m.RolesPage })));
const OAuthClientsPage = lazyPage(() => import('./pages/OAuthClientsPage').then((m) => ({ default: m.OAuthClientsPage })));
const MonitorPage = lazyPage(() => import('./pages/MonitorPage').then((m) => ({ default: m.MonitorPage })));
const QueuesPage = lazyPage(() => import('./pages/QueuesPage').then((m) => ({ default: m.QueuesPage })));
const DashboardPage = lazyPage(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const StubPage = lazyPage(() => import('./pages/StubPage').then((m) => ({ default: m.StubPage })));
const ApiWebhooksPage = lazyPage(() => import('./pages/ApiWebhooksPage').then((m) => ({ default: m.ApiWebhooksPage })));
const ConnectionsPage = lazyPage(() => import('./pages/ConnectionsPage').then((m) => ({ default: m.ConnectionsPage })));
const MarketplacePage = lazyPage(() => import('./pages/MarketplacePage').then((m) => ({ default: m.MarketplacePage })));
const MeshPage = lazyPage(() => import('./pages/MeshPage').then((m) => ({ default: m.MeshPage })));
const SchemaBrowserPage = lazyPage(() => import('./pages/SchemaBrowserPage').then((m) => ({ default: m.SchemaBrowserPage })));
const LiveTracePage = lazyPage(() => import('./pages/LiveTracePage').then((m) => ({ default: m.LiveTracePage })));
const LogsPage = lazyPage(() => import('./pages/LogsPage').then((m) => ({ default: m.LogsPage })));
const AuditLogPage = lazyPage(() => import('./pages/AuditLogPage').then((m) => ({ default: m.AuditLogPage })));
const PoliciesPage = lazyPage(() => import('./pages/PoliciesPage').then((m) => ({ default: m.PoliciesPage })));
const WorkspaceAccessRequestPage = lazyPage(() => import('./pages/WorkspaceAccessRequestPage').then((m) => ({ default: m.WorkspaceAccessRequestPage })));
const WorkspaceSettingsPage = lazyPage(() => import('./pages/WorkspaceSettingsPage').then((m) => ({ default: m.WorkspaceSettingsPage })));
const BrandingSettingsPage = lazyPage(() => import('./pages/BrandingSettingsPage').then((m) => ({ default: m.BrandingSettingsPage })));
const GitopsSettingsPage = lazyPage(() => import('./pages/GitopsSettingsPage').then((m) => ({ default: m.GitopsSettingsPage })));
const FirstWorkspacePage = lazyPage(() => import('./pages/FirstWorkspacePage').then((m) => ({ default: m.FirstWorkspacePage })));
const OnboardingWelcomePage = lazyPage(() => import('./onboarding/WelcomePage').then((m) => ({ default: m.WelcomePage })));
const OnboardingEnvironmentsPage = lazyPage(() => import('./onboarding/EnvironmentsPage').then((m) => ({ default: m.EnvironmentsPage })));
const OnboardingConnectPage = lazyPage(() => import('./onboarding/ConnectPage').then((m) => ({ default: m.ConnectPage })));
const OnboardingTemplatePage = lazyPage(() => import('./onboarding/TemplatePage').then((m) => ({ default: m.TemplatePage })));
const OnboardingSuccessPage = lazyPage(() => import('./onboarding/SuccessPage').then((m) => ({ default: m.SuccessPage })));
const OnboardingPipePage = lazyPage(() => import('./onboarding/PipePage').then((m) => ({ default: m.PipePage })));
const OnboardingFirstFlowPage = lazyPage(() => import('./onboarding/FirstFlowPage').then((m) => ({ default: m.FirstFlowPage })));

/** The full authenticated app: onboarding gate + app shell + route table.
 *  Extracted verbatim so the auth screens can render OUTSIDE it. */
function ShellApp() {
  const cloud = useCloud();
  const { pathname } = useLocation();
  return (
    <WorkspaceAccessRedirector>
      <OnboardingRedirector>
        <AppShellSpa>
          {/* A page Pipebase Cloud leaves out, reached by its URL. */}
          {cloud && isCloudHiddenPath(pathname) ? <NotOnCloudPage /> : <ErrorBoundary key={pathname} scope="page" fallback={(err) => <PageError err={err} />}><Suspense fallback={<PageLoading />}><Routes>
            {/* Operate */}
            <Route path="/" element={<DashboardPage />} />
            <Route path="/monitor" element={<MonitorPage />} />
            <Route path="/queues" element={<QueuesPage />} />
            <Route path="/trace" element={<LiveTracePage />} />
            <Route path="/logs" element={<LogsPage />} />

            {/* Build */}
            <Route path="/flows" element={<FlowList />} />
            <Route path="/flows/:id" element={<FlowList />} />
            <Route path="/value-mappings" element={<ValueMappingListPage />} />
            <Route path="/value-mappings/:id" element={<ValueMappingEditPage />} />
            <Route path="/mappings" element={<MessageMappingListPage />} />
            <Route path="/mappings/:id" element={<MessageMappingEditPage />} />
            <Route path="/event-designs" element={<EventDesignListPage />} />
            <Route path="/event-design/:id" element={<EventDesignEditPage />} />

            {/* Connect */}
            <Route path="/connections" element={<ConnectionsPage />} />
            <Route path="/mesh" element={<MeshPage />} />
            <Route path="/schemas" element={<SchemaBrowserPage />} />
            <Route path="/oauth-clients" element={<OAuthClientsPage />} />
            <Route path="/parameters" element={<ParametersListPage />} />

            {/* Govern */}
            <Route path="/cutover" element={<CutoverOverviewPage />} />
            <Route path="/audit" element={<AuditLogPage />} />
            <Route path="/event-catalog" element={<EventCatalogPage />} />
            <Route path="/roles" element={<RolesPage />} />
            <Route path="/policies" element={<PoliciesPage />} />

            {/* Extend */}
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/api-webhooks" element={<ApiWebhooksPage />} />

            {/* Organization */}
            <Route path="/settings" element={<Settings />} />
            <Route path="/billing" element={<Navigate to="/" replace />} />
            <Route path="/runtimes" element={<RuntimeListPage />} />
            <Route path="/runtimes/matrix" element={<FleetMatrixPage />} />
            <Route path="/runtimes/new" element={<RuntimeEditPage />} />
            <Route path="/runtimes/:id" element={<RuntimeEditPage />} />
            <Route path="/pools/new" element={<PoolNewPage />} />
            <Route path="/pools/:id" element={<PoolDetailPage />} />

            {/* Editor routes — AppShell hides its chrome for these. */}
            <Route path="/edit/:flowId" element={<FlowEditor />} />
            <Route path="/edit-next/:flowId" element={<FlowEditorNext />} />
            <Route path="/edit/:flowId/cutover" element={<FlowCutoverPage />} />
            <Route path="/edit/:flowId/params" element={<Navigate to="../cutover" replace />} />
            <Route path="/edit/:flowId/runs" element={<RunHistoryPage />} />
            <Route path="/edit/:flowId/slo" element={<FlowSloPage />} />

            {/* Multi-tenancy onboarding/access landing. */}
            <Route path="/welcome/request-access" element={<WorkspaceAccessRequestPage />} />
            <Route path="/onboarding/first-workspace" element={<FirstWorkspacePage />} />
            <Route path="/settings/workspace" element={<WorkspaceSettingsPage />} />
            <Route path="/settings/branding" element={<BrandingSettingsPage />} />
            <Route path="/settings/gitops" element={<GitopsSettingsPage />} />

            {/* Onboarding wizard (5-step). */}
            <Route path="/onboarding/welcome" element={<OnboardingWelcomePage />} />
            <Route path="/onboarding/environments" element={<OnboardingEnvironmentsPage />} />
            <Route path="/onboarding/connect" element={<OnboardingConnectPage />} />
            <Route path="/onboarding/template" element={<OnboardingTemplatePage />} />
            <Route path="/onboarding/pipe" element={<OnboardingPipePage />} />
            <Route path="/onboarding/first-flow" element={<OnboardingFirstFlowPage />} />
            <Route path="/onboarding/success" element={<OnboardingSuccessPage />} />
            <Route path="/onboarding" element={<Navigate to="/onboarding/welcome" replace />} />

            {/* Legacy stub destination (kept for old links). */}
            <Route path="/not-implemented/:id" element={<StubPage />} />

            {/* Anything else falls through to the Dashboard. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes></Suspense></ErrorBoundary>}
        </AppShellSpa>
      </OnboardingRedirector>
    </WorkspaceAccessRedirector>
  );
}

/** Auth gate. Renders the bare setup / login screens OUTSIDE the app shell,
 *  or the full ShellApp once the user is past the gate.
 *
 *   needsSetup            → /auth/setup (first-admin wizard)
 *   enforced && !user     → /auth/login?returnTo=… (in-app login)
 *   otherwise             → ShellApp
 *
 *  There is NO hard SSO redirect — the login page offers the SSO button. */
export function AuthedApp() {
  const { user, setupState, sessionExpired, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="auth-screen ip-shell" data-testid="auth-loading" />;
  const path = loc.pathname;
  if (setupState?.needsSetup && path !== '/auth/setup') {
    return <Navigate to="/auth/setup" replace />;
  }
  if (path === '/auth/setup') return <AuthSetupPage />;
  const enforced = setupState?.enforced ?? false;
  if (enforced && !user && path !== '/auth/login' && !setupState?.needsSetup) {
    const returnTo = encodeURIComponent(loc.pathname + loc.search);
    // Append the idle-timeout reason so the login page can explain why the
    // user was bounced here (a live session expired vs never signed in).
    const reason = sessionExpired ? '&reason=idle-timeout' : '';
    return <Navigate to={`/auth/login?returnTo=${returnTo}${reason}`} replace />;
  }
  if (path === '/auth/login') return <AuthLoginPage />;
  return <ShellApp />;
}

export function App() {
  return (
    <AuthProvider>
      <ActiveWorkspaceProvider>
      <WorkspaceBrandingProvider>
      <WorkspaceFeaturesProvider>
      <ActiveRuntimeProvider>
        <ToastProvider>
          <AuthedApp />
        </ToastProvider>
      </ActiveRuntimeProvider>
      </WorkspaceFeaturesProvider>
      </WorkspaceBrandingProvider>
      </ActiveWorkspaceProvider>
    </AuthProvider>
  );
}

/** Shown for the moment a page's code is loading. */
/** A page that could not load or crashed: the shell stays, and so does the nav. */
function PageError({ err }: { err: Error }) {
  return (
    <div className="page">
      <EmptyState
        data-testid="page-error"
        title="This page could not load"
        description={`Reload to try again. If a new version was just deployed, the reload fetches it. (${err.message.slice(0, 160)})`}
        action={
          <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
            Reload
          </button>
        }
      />
    </div>
  );
}

function PageLoading() {
  return (
    <div className="page t-3" role="status" aria-live="polite" style={{ fontSize: 13 }}>
      Loading…
    </div>
  );
}
